import {
  AttachMoneyOutlined,
  DesignServicesOutlined,
  Favorite,
  FavoriteBorder,
  SchoolOutlined,
} from '@mui/icons-material';
import { Divider } from '@mui/material';
import { CoreBox, CoreButton, CoreGrid, CoreTooltip, CoreTypography } from '@youscience/core';
import { useState } from 'react';

import EllipsisTypography from '@components/EllipsisTypography';
import { FitBullets, FitTypes, fitConstants, fitTypeLabels } from '@components/FitBullets/FitBullets';

import { DISCOVERY_URL } from '@constants/externalRoutes';

import { useSaveFavoriteCareer, useUnsaveFavoriteCareer } from '@hooks/Career';

import { Career } from '@services/career';

import { StyledCard, StyledContainerLink, StyledEmployerLogo, sxStyles } from './CareerCard.styles';

export interface CareerCardProps {
  career: Career;
  userId: string;
  careerUrl?: string;
  fitType?: FitTypes;
}

export const CareerCard = ({ career, careerUrl, userId, fitType }: CareerCardProps) => {
  const [saved, setSaved] = useState<boolean>(career.saved);
  const { mutateAsync: saveFavoriteCareer, isPending: isSaving } = useSaveFavoriteCareer(career.onetsoc_code, userId);
  const { mutateAsync: unsaveFavoriteCareer, isPending: isUnsaving } = useUnsaveFavoriteCareer(
    career.onetsoc_code,
    userId,
  );
  const overallFitValue = career.match?.ranked_overall_fit ?? 0;
  const averageSalary = career.salary.national.average ? Number(career.salary.national.average) : 0;
  const unknown = 'Unknown';

  const handleToggleFavorite = async () => {
    if (isSaving || isUnsaving) return;

    setSaved(!saved);
    if (saved) {
      await unsaveFavoriteCareer();
    } else {
      await saveFavoriteCareer();
    }
  };

  const handleMoreDetails = () => {
    window.open(`${DISCOVERY_URL}/careers#/${career.onetsoc_code}`, '_blank');
  };

  return (
    <StyledContainerLink href={careerUrl} target="_blank" rel="noreferrer">
      <StyledCard>
        <CoreBox sx={sxStyles.boxPadding} onClick={handleMoreDetails}>
          <CoreGrid item container xs={12}>
            <CoreGrid item xs={8}>
              <CoreTypography variant="caption">
                {fitConstants.get(overallFitValue)?.adjective ?? unknown} {fitTypeLabels[FitTypes.Overall]}
              </CoreTypography>
            </CoreGrid>

            <CoreGrid item xs={4} sx={{ paddingTop: '6px' }}>
              <FitBullets fit={overallFitValue} type={FitTypes.Overall} />
            </CoreGrid>
          </CoreGrid>
        </CoreBox>

        <Divider />

        <CoreBox sx={sxStyles.logoCover} onClick={handleMoreDetails}>
          <StyledEmployerLogo src={career.images[0].url} alt={`${career.title} logo`} />
        </CoreBox>

        <Divider />

        <CoreBox sx={sxStyles.boxPadding} onClick={handleMoreDetails}>
          <CoreTooltip title={career.title} disableInteractive>
            <EllipsisTypography sx={sxStyles.title} stringCount={1} variant="h5">
              {career.title}
            </EllipsisTypography>
          </CoreTooltip>
        </CoreBox>

        <Divider />

        <CoreBox sx={sxStyles.boxPadding} onClick={handleMoreDetails}>
          <CoreGrid item container xs={12} sx={{ alignItems: 'center' }}>
            <CoreGrid item xs={1.5}>
              <SchoolOutlined sx={sxStyles.icon} />
            </CoreGrid>

            <CoreGrid item xs={7.5}>
              <CoreTypography variant="body2">Education Investment</CoreTypography>
            </CoreGrid>

            <CoreGrid item xs={3} sx={{ textAlign: 'right' }}>
              <CoreTypography variant="body2" sx={sxStyles.detailNote}>
                {career.investment.education.short_label === 'None'
                  ? 'None'
                  : `${career.investment.education.short_label} YRS`}
              </CoreTypography>
            </CoreGrid>
          </CoreGrid>

          <CoreGrid item container xs={12} sx={{ alignItems: 'center' }}>
            <CoreGrid item xs={1.5}>
              <DesignServicesOutlined sx={sxStyles.icon} />
            </CoreGrid>

            <CoreGrid item xs={7.5}>
              <CoreTypography variant="body2">Projected Openings</CoreTypography>
            </CoreGrid>

            <CoreGrid item xs={3} sx={{ textAlign: 'right' }}>
              <CoreTypography variant="body2" sx={sxStyles.detailNote}>
                {career.employment.national.openings}
              </CoreTypography>
            </CoreGrid>
          </CoreGrid>

          <CoreGrid item container xs={12} sx={{ alignItems: 'center' }}>
            <CoreGrid item xs={1.5}>
              <AttachMoneyOutlined sx={sxStyles.icon} />
            </CoreGrid>

            <CoreGrid item xs={7.5}>
              <CoreTypography variant="body2">Average Salary</CoreTypography>
            </CoreGrid>

            <CoreGrid item xs={3} sx={{ textAlign: 'right' }}>
              <CoreTypography variant="body2" sx={sxStyles.detailNote}>
                {`${Math.round(averageSalary / 1000)}K`}
              </CoreTypography>
            </CoreGrid>
          </CoreGrid>
        </CoreBox>

        <Divider />

        <CoreBox sx={sxStyles.buttonsContainer}>
          <CoreButton sx={sxStyles.favoriteBtn} variant="text" onClick={handleToggleFavorite}>
            {saved ? <Favorite /> : <FavoriteBorder />}
          </CoreButton>
        </CoreBox>
      </StyledCard>
    </StyledContainerLink>
  );
};
